import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import { GUIDES, getGuide } from "../data/guides";
import { money } from "../lib/format";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  if (!guide) {
    return (
      <div className="container-x py-24 text-center" data-testid="guide-not-found">
        <SEO title="Guide not found" path={`/guides/${slug}`} noIndex />
        <h1 className="font-display text-3xl">Guide not found</h1>
        <Link to="/guides" className="btn-primary mt-6 inline-flex">
          Back to all guides
        </Link>
      </div>
    );
  }

  const related = GUIDES.filter((g) => g.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.subtitle,
    image: guide.image,
    url: `${SITE_URL}/guides/${guide.slug}`,
    datePublished: "2026-01-01",
    dateModified: new Date().toISOString().split("T")[0],
    author: { "@type": "Organization", name: "UpgradeCost" },
    publisher: {
      "@type": "Organization",
      name: "UpgradeCost",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/guides/${guide.slug}` },
  };

  return (
    <article data-testid="guide-detail-page" className="bg-grain">
      <SEO
        title={guide.title}
        description={guide.subtitle}
        path={`/guides/${guide.slug}`}
        image={guide.image}
        type="article"
        jsonLd={articleJsonLd}
      />
      {guide.image && (
        <div className="relative h-[280px] sm:h-[380px] lg:h-[440px] overflow-hidden border-b border-line">
          <img
            src={guide.image}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.parentElement.style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/40 to-transparent" />
        </div>
      )}
      <div className="container-x py-12 lg:py-16 max-w-4xl">
        <Link
          to="/guides"
          className="overline hover:text-brand transition"
          data-testid="guide-back-link"
        >
          ← All cost guides
        </Link>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]">
          {guide.h1}
        </h1>
        <p className="mt-5 text-lg text-ink-muted leading-relaxed">
          {guide.subtitle}
        </p>

        {/* Range card */}
        <div className="surface-card mt-10 grid grid-blueprint grid-cols-3" data-testid="guide-range">
          <RangeBox label="Low" value={money(guide.range.low)} />
          <RangeBox label="Expected" value={money(guide.range.mid)} highlight />
          <RangeBox label="High" value={money(guide.range.high)} />
        </div>
        <p className="font-mono text-xs text-ink-faint mt-3">
          {guide.rangeLabel}
        </p>

        {/* Intro */}
        <p className="mt-12 text-lg leading-relaxed">{guide.intro}</p>

        {/* Sections */}
        <div className="mt-14 space-y-12">
          {guide.sections.map((s, idx) => (
            <section
              key={s.title}
              data-testid={`guide-section-${idx}`}
              className="border-l-2 border-brand pl-7"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
                {s.title}
              </h2>
              <p className="text-ink-muted leading-relaxed text-lg">{s.body}</p>
            </section>
          ))}
        </div>

        {/* CTA */}
        <div className="surface-card mt-16 p-10 text-center bg-bg-alt" data-testid="guide-cta">
          <Calculator className="text-brand mx-auto mb-4" size={32} />
          <h3 className="font-display text-3xl font-semibold tracking-tight">
            Get a number for <em>your</em> bathroom.
          </h3>
          <p className="mt-3 text-ink-muted max-w-md mx-auto">
            Two minutes, no signup to see your range, and a printable brief on the other side.
          </p>
          <Link to="/calculator" className="btn-primary mt-7 px-7 py-4">
            Open the calculator
            <ArrowRight size={16} />
          </Link>
        </div>

        {/* Related */}
        <div className="mt-20">
          <p className="overline mb-6">Keep reading</p>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                data-testid={`related-${g.slug}`}
                className="surface-card p-5 hover:border-brand transition"
              >
                <p className="font-display font-semibold leading-snug">
                  {g.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

const RangeBox = ({ label, value, highlight }) => (
  <div className={`p-6 bg-surface ${highlight ? "bg-brand-tint" : ""}`}>
    <p className="overline mb-2">{label}</p>
    <p className={`font-display text-3xl font-semibold tracking-tighter ${highlight ? "text-brand" : ""}`}>
      {value}
    </p>
  </div>
);
